"use client";

import { BACKEND_HEADER, BACKEND_POSTGRES, resolveBrowserBackend } from "@/features/postgres-backend/feature-flag";
import type { AppRouter } from "@/features/postgres-backend/server/api/root";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { createTRPCReact, type CreateTRPCReact } from "@trpc/react-query";

import { memo, type ReactNode, useState } from "react";
import superjson from "superjson";

export const postgresBackendTrpc: CreateTRPCReact<AppRouter, unknown> =
  createTRPCReact<AppRouter>();

/** What each Postgres backend procedure takes and answers, as its readers see it. */
export type PostgresBackendInputs = inferRouterInputs<AppRouter>;
export type PostgresBackendOutputs = inferRouterOutputs<AppRouter>;

export const TRPCProvider = memo(function TRPCProvider(props: { children: ReactNode }) {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    postgresBackendTrpc.createClient({
      links: [
        httpBatchLink({
          url: "/api/postgres-backend/trpc",
          fetch(url, options) {
            return globalThis.fetch(url, { ...options, credentials: "same-origin" });
          },
          headers() {
            return resolveBrowserBackend() === BACKEND_POSTGRES
              ? { [BACKEND_HEADER]: BACKEND_POSTGRES }
              : {};
          },
          transformer: superjson,
        }),
      ],
    }),
  );

  return (
    <postgresBackendTrpc.Provider client={trpcClient} queryClient={queryClient} data-component="TRPCProvider" data-location="src/features/postgres-backend/client/trpc.tsx">
      <QueryClientProvider client={queryClient}>{props.children}</QueryClientProvider>
    </postgresBackendTrpc.Provider>
  );
});
